// src/oura/merchant/homeFigures.tsx
//
// Home's three figures, each a value and the one line that goes with it: how
// AI names them, this month's work, and visits from Google. Worked out once
// here for the redesigned number tiles (HomeNumbers.tsx) and for the tiles Home
// shows with the switch off, so the two cannot disagree.

import type { ReactNode } from "react";
import type { ClientPagePayload, ClientStats } from "@service/aeo/clientReview";
import { Delta, OfValue } from "@oura/ui";
import { PctChange } from "../components/search/PctChange";
import { dayDate, shortDateOrNull } from "../lib/oura";
import { monthName } from "../visibility/format";
import { asClause, measuredItems, planDone, type ComingDate, type MerchantLive } from "./model";
import { BRAND_WAIT_LINE } from "./brandWait";
import { aiBandLine } from "./reportWording";

/** A value at tile size and the one line that goes with it. */
export interface Figure {
    /** A number, <OfValue> or a word; ignored while loading. */
    value: ReactNode;
    sub?: ReactNode;
    /** The value is on its way: a placeholder bar, never a 0. */
    loading?: boolean;
    /** What a folded card still says. */
    fact?: string;
    /** A tile's accessible name, when the label alone does not say where it goes. */
    ariaLabel?: string;
}

/**
 * The named count merchant Visibility shows. A change only where the server
 * says the previous scan asked the same questions (rule 2); otherwise the day
 * it was checked.
 */
export function foundFigure({
    live,
    aiCheck,
    nextCheck,
    scheduled,
    planStarted,
    waitsForBrand = false,
}: {
    live: MerchantLive | null | undefined;
    aiCheck: ClientStats["aiCheck"];
    /** The next monthly report's date, which brings the AI check. */
    nextCheck: string | null;
    /** A schedule is published, so a missing date means none is left this term. */
    scheduled: boolean;
    planStarted: boolean;
    /** An Online client whose first check waits for brand checks (§2.4a). */
    waitsForBrand?: boolean;
}): Figure {
    if (live === undefined) return { value: null, loading: true, ariaLabel: "How you're found, loading. Open Visibility." };
    const found = live?.found;
    if (found) {
        const checked = shortDateOrNull(found.checkedAt);
        // "since August", the words merchant Visibility's sentence uses.
        const since = found.previous ? monthName(found.previous.at) : "";
        return {
            value: <OfValue n={found.named} of={found.of} />,
            sub: found.previous ? (
                <Delta value={found.named - found.previous.named} comparable suffix={since ? `since ${since}` : undefined} />
            ) : (
                checked && `Checked ${checked}`
            ),
            fact: `${found.named} of ${found.of} questions`,
            ariaLabel: `How you're found: named in ${found.named} of ${found.of} questions. Open Visibility.`,
        };
    }
    // The detail could not be read: say where the count lives, not that it is missing.
    if (live === null) return { value: "–", sub: "Open Visibility to see how AI names you." };
    // A band with no scored count: the check ran, so the figure states its
    // finding (staging f73b9933) rather than denying the check above it.
    if (aiCheck.band) return { value: "Checked", sub: aiBandLine(aiCheck.band) };
    if (waitsForBrand) return { value: "Not checked yet", sub: BRAND_WAIT_LINE };
    const next = dayDate(nextCheck);
    return {
        value: "Not checked yet",
        sub:
            // The server's reason (ourteam-backend#5076, staging f73b9933):
            // nothing has been scanned yet, which a date would not say.
            aiCheck.bandAbsent === "no_assessment"
                ? "Your first scan has not run yet"
                : next
                    ? `First AI check ${next}`
                    : // No date. Reports' words when the schedule says why
                      // (none left this term, or no plan yet); otherwise
                      // nothing, since naming a time would be an invention
                      // (main 9b4bb460).
                      scheduled
                        ? "No AI check left this term"
                        : !planStarted
                            ? "Your first AI check is dated when your plan starts"
                            : undefined,
    };
}

export function workFigure({ payload, planStarted, next }: { payload: ClientPagePayload; planStarted: boolean; next: ComingDate | null }): Figure {
    if (!planStarted) return { value: "Not started", sub: "Your work shows here once your plan starts." };
    // Over measured work only (staging 3fdc24e6): a hand-reported line is never
    // "done", so counting it would cap the figure below its own total.
    const measured = measuredItems(payload.plan?.items ?? []);
    const done = planDone(measured);
    if (measured.length === 0) {
        const day = dayDate(next?.date);
        const nextLine = next && day ? `Next: ${day}, ${asClause(next.label)}` : null;
        return { value: "By hand", sub: nextLine ?? "We account for this month's work by hand." };
    }
    return {
        value: <OfValue n={done} of={measured.length} />,
        sub: `${measured.length - done} to go`,
        fact: `${done} of ${measured.length}`,
        ariaLabel: `This month: ${done} of ${measured.length} measured pieces of work done. Open Work.`,
    };
}

/**
 * Who came to them from Google: Search Console's clicks over its window, and
 * the change on the window before (the same count both times, so the change
 * is real). Clicks from AI apps wait for Analytics to split visits by source;
 * nothing stands in for them.
 */
export function visitsFigure(search: ClientStats["search"]): Figure {
    if (!search) return { value: "–", sub: "Shows once Google reports your clicks." };
    const clicks = search.clicks.toLocaleString("en-GB");
    return {
        value: <OfValue n={clicks} unit="clicks" />,
        sub:
            // As a percentage, the way Search performance states the same
            // change: "▲ 56" here beside "▲ 15.6%" there read as two
            // different numbers (usability test, 14 Oct 2026).
            search.clicksPrevious ? (
                <PctChange percent={((search.clicks - search.clicksPrevious) / search.clicksPrevious) * 100} days={search.windowDays} />
            ) : search.clicksPrevious === 0 ? (
                <Delta value={search.clicks} comparable suffix={`on the ${search.windowDays} days before`} />
            ) : (
                `Last ${search.windowDays} days`
            ),
        fact: `${clicks} clicks`,
        ariaLabel: `Visits from Google: ${clicks} clicks from Google search in the last ${search.windowDays} days. Open the searches behind them.`,
    };
}
